import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from "recharts";
import { IndianRupee, TrendingUp, TrendingDown, Clock, Users, FileText, AlertTriangle, ArrowDownLeft, ArrowUpRight, Wallet } from "lucide-react";

const COLORS = ["#4338ca", "#10b981", "#f59e0b", "#ef4444"];

function CashflowKPI({ testId, label, icon: Icon, color, amount, changePct, positiveIsGood = true, highlight = false }) {
  const isUp = changePct != null && changePct >= 0;
  // For "Money Out", an increase is bad. Flip the trend color logic accordingly.
  const trendGood = positiveIsGood ? isUp : !isUp;
  const trendColor = changePct == null ? "#94a3b8" : trendGood ? "#10b981" : "#ef4444";
  return (
    <div
      className={`rounded-xl border p-3 ${highlight ? "border-[#c7d2fe] bg-[#eef2ff]" : "border-[#e2e8f0] bg-white"}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div
          className="h-6 w-6 rounded-md flex items-center justify-center"
          style={{ backgroundColor: color + "1A" }}
        >
          <Icon className="h-3.5 w-3.5" style={{ color }} />
        </div>
        <span className="text-xs text-[#64748b] font-medium">{label}</span>
      </div>
      <p className="text-lg font-outfit font-bold text-[#0f172a] font-mono">{formatINR(amount)}</p>
      {changePct != null && (
        <p className="text-[10px] mt-1 font-medium" style={{ color: trendColor }} data-testid={`${testId}-change`}>
          {isUp ? "+" : ""}
          {changePct}% vs prev 30d
        </p>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cashflow, setCashflow] = useState(null);
  const [cashflowLoading, setCashflowLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch((err) => console.error("Failed to load stats:", err))
      .finally(() => setLoading(false));

    api
      .get("/dashboard/cashflow?days=30")
      .then((r) => setCashflow(r.data))
      .catch((err) => console.error("Failed to load cashflow:", err))
      .finally(() => setCashflowLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-8 animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-48" />
        <div className="grid grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const gstData = [
    { name: "CGST", value: stats?.total_cgst || 0 },
    { name: "SGST", value: stats?.total_sgst || 0 },
    { name: "IGST", value: stats?.total_igst || 0 },
  ].filter((d) => d.value > 0);

  // Outstanding now combines receivables (clients owe us) + payables (we owe vendors)
  // so the dashboard shows the full cash-flow picture at a glance. A small
  // breakdown caption beneath the headline preserves clarity — the two are
  // opposite balance-sheet entries and the user needs to see both numbers.
  const outstandingTotal = stats?.outstanding_total ?? (stats?.outstanding || 0);
  const outstandingReceivables = stats?.outstanding_receivables ?? (stats?.outstanding || 0);
  const outstandingPayables = stats?.outstanding_payables || 0;

  const statCards = [
    { label: "Total Revenue", value: formatINR(stats?.total_revenue), icon: IndianRupee, color: "#4338ca" },
    {
      label: "Outstanding",
      value: formatINR(outstandingTotal),
      icon: Clock,
      color: "#f59e0b",
      // Sub-caption: split the headline into the two underlying flows so the
      // accountant sees who-owes-whom without leaving the dashboard.
      subline: (outstandingReceivables > 0 || outstandingPayables > 0)
        ? `Recv ${formatINR(outstandingReceivables)} · Pay ${formatINR(outstandingPayables)}`
        : null,
    },
    { label: "Total Invoices", value: stats?.invoice_count || 0, icon: FileText, color: "#10b981" },
    { label: "Total Clients", value: stats?.client_count || 0, icon: Users, color: "#3b82f6" },
  ];

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">
          Welcome back, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-sm text-[#64748b] mt-1">
          {user?.business_name} &middot; {user?.home_state}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {statCards.map((card) => (
          <Card
            key={card.label}
            className="bg-white border-[#e2e8f0] shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-shadow duration-200"
            data-testid={`stat-${card.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[#64748b] font-medium">{card.label}</span>
                <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: card.color + "15" }}>
                  <card.icon className="h-4 w-4" style={{ color: card.color }} />
                </div>
              </div>
              <p className="text-2xl font-outfit font-bold text-[#0f172a]">{card.value}</p>
              {card.subline && (
                <p
                  className="text-[11px] text-[#64748b] mt-1.5 font-medium"
                  data-testid={`stat-${card.label.toLowerCase().replace(/\s/g, "-")}-breakdown`}
                >
                  {card.subline}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Net Cashflow Snapshot */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm overflow-hidden" data-testid="cashflow-snapshot">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="font-outfit text-lg font-semibold text-[#0f172a] flex items-center gap-2">
              <Wallet className="h-4 w-4 text-[#4338ca]" /> Net Cashflow
              <span className="text-xs font-normal text-[#64748b] ml-1">Last 30 days</span>
            </CardTitle>
            {cashflow && (
              <span className="text-xs text-[#64748b] font-mono" data-testid="cashflow-range">
                {cashflow.from_date} &rarr; {cashflow.to_date}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {cashflowLoading ? (
            <div className="h-[160px] animate-pulse bg-[#f1f5f9] rounded-lg" />
          ) : cashflow ? (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
              {/* KPI Tiles */}
              <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <CashflowKPI
                  testId="cashflow-in"
                  label="Money In"
                  icon={ArrowDownLeft}
                  color="#10b981"
                  amount={cashflow.in_total}
                  changePct={cashflow.in_change_pct}
                  positiveIsGood
                />
                <CashflowKPI
                  testId="cashflow-out"
                  label="Money Out"
                  icon={ArrowUpRight}
                  color="#ef4444"
                  amount={cashflow.out_total}
                  changePct={cashflow.out_change_pct}
                  positiveIsGood={false}
                />
                <CashflowKPI
                  testId="cashflow-net"
                  label="Net"
                  icon={cashflow.net >= 0 ? TrendingUp : TrendingDown}
                  color={cashflow.net >= 0 ? "#4338ca" : "#ef4444"}
                  amount={cashflow.net}
                  changePct={null}
                  highlight
                />
              </div>

              {/* Sparkline */}
              <div className="lg:col-span-3 h-[160px]" data-testid="cashflow-sparkline">
                {cashflow.series?.some((d) => d.in > 0 || d.out > 0) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={cashflow.series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                      <defs>
                        <linearGradient id="inGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="outGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: "#94a3b8" }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(d) => d.slice(5)}
                        minTickGap={24}
                      />
                      <YAxis hide />
                      <Tooltip
                        formatter={(value, name) => [formatINR(value), name === "in" ? "In" : "Out"]}
                        labelFormatter={(label) => label}
                        contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                      />
                      <Area type="monotone" dataKey="in" stroke="#10b981" strokeWidth={2} fill="url(#inGrad)" />
                      <Area type="monotone" dataKey="out" stroke="#ef4444" strokeWidth={2} fill="url(#outGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-[#64748b]">
                    No receipts or payments in the last 30 days.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-[120px] flex items-center justify-center text-sm text-[#64748b]">
              Cashflow data unavailable.
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-white border-[#e2e8f0] shadow-sm" data-testid="monthly-revenue-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-lg font-semibold text-[#0f172a] flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#4338ca]" /> Monthly Revenue (INR)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.monthly_revenue?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={stats.monthly_revenue}>
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => [formatINR(value), "Revenue"]} labelStyle={{ color: "#0f172a" }} />
                  <Bar dataKey="revenue" fill="#4338ca" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-sm text-[#64748b]">
                No revenue data yet. Create your first invoice to see charts.
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border-[#e2e8f0] shadow-sm" data-testid="gst-liability-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-lg font-semibold text-[#0f172a]">GST Liability</CardTitle>
          </CardHeader>
          <CardContent>
            {gstData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={gstData} cx="50%" cy="50%" outerRadius={80} innerRadius={50} dataKey="value" paddingAngle={3}>
                      {gstData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatINR(value)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-2">
                  {gstData.map((d, i) => (
                    <div key={d.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: COLORS[i] }} />
                        <span className="text-[#64748b]">{d.name}</span>
                      </div>
                      <span className="font-medium font-mono text-[#0f172a]">{formatINR(d.value)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-sm text-[#64748b]">No GST data yet.</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Aging Summary */}
      {stats?.aging_summary && (
        <Card className="bg-white border-[#e2e8f0] shadow-sm" data-testid="aging-summary">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-lg font-semibold text-[#0f172a] flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[#f59e0b]" /> Overdue Invoice Aging
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { key: "0_30", label: "0-30 Days", color: "#10b981", bg: "#10b981" },
                { key: "31_60", label: "31-60 Days", color: "#f59e0b", bg: "#f59e0b" },
                { key: "61_90", label: "61-90 Days", color: "#f97316", bg: "#f97316" },
                { key: "90_plus", label: "90+ Days", color: "#ef4444", bg: "#ef4444" },
              ].map((bucket) => {
                const data = stats.aging_summary[bucket.key] || { count: 0, total: 0 };
                return (
                  <div
                    key={bucket.key}
                    className="rounded-xl border border-[#e2e8f0] p-4 relative overflow-hidden"
                    data-testid={`aging-${bucket.key}`}
                  >
                    <div className="absolute top-0 left-0 w-1 h-full" style={{ backgroundColor: bucket.bg }} />
                    <p className="text-xs text-[#64748b] font-medium mb-1">{bucket.label}</p>
                    <p className="text-lg font-outfit font-bold text-[#0f172a]">{data.count}</p>
                    <p className="text-xs font-mono text-[#64748b] mt-0.5">{formatINR(data.total)}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white border-[#e2e8f0] shadow-sm" data-testid="recent-invoices">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-lg font-semibold text-[#0f172a]">Recent Invoices</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recent_invoices?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0]">
                    <th className="text-left py-3 text-[#64748b] font-medium">Invoice #</th>
                    <th className="text-left py-3 text-[#64748b] font-medium">Client</th>
                    <th className="text-left py-3 text-[#64748b] font-medium">Date</th>
                    <th className="text-right py-3 text-[#64748b] font-medium">Amount</th>
                    <th className="text-right py-3 text-[#64748b] font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_invoices.map((inv) => (
                    <tr key={inv.invoice_id} className="border-b border-[#f1f5f9] last:border-0">
                      <td className="py-3 font-mono text-xs text-[#4338ca]">{inv.invoice_number}</td>
                      <td className="py-3 text-[#0f172a]">{inv.client_name}</td>
                      <td className="py-3 text-[#64748b]">{inv.date}</td>
                      <td className="py-3 text-right font-mono font-medium text-[#0f172a]">{formatINR(inv.total)}</td>
                      <td className="py-3 text-right">
                        <Badge
                          className={
                            inv.status === "paid"
                              ? "bg-[#10b981] text-white"
                              : inv.status === "sent"
                              ? "bg-[#3b82f6] text-white"
                              : "bg-[#f1f5f9] text-[#64748b]"
                          }
                        >
                          {inv.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-[#64748b] py-8 text-center">
              No invoices yet. Use the Command Center to create your first invoice.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
